import { MktWrapper } from '@playbooks/components/mkt/mkt-wrapper';
import { DisplaySection, DisplaySectionBody } from '@playbooks/molecules/panels';
import { Link } from '@playbooks/ui/components/link';

import { LegalHero } from 'mkt/legal/legal-hero';
import { formatDate } from 'utils';

const MktPrivacyRoute = ({ app, ssr, workspace, router, search, session, store, toast }) => {
	// Components
	const SupportEmail = () => (
		<Link variant='link' href='mailto:support@playbooks.ai' className='text-foreground hover:underline'>
			support@playbooks.ai
		</Link>
	);

	// Render
	return (
		<>
			<LegalHero title='Privacy Policy' subtitle={`Last updated: ${formatDate('01/01/2026', 'MMMM Do, YYYY')}`} />

			<div className='mx-auto px-3 max-w-xxl my-4'>
				<DisplaySection className='py-8'>
					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<p className='text-sm text-foreground-body'>
							This Privacy Policy (“Privacy Policy”) describes how Playbooks.xyz and its subsidiaries and affiliates
							(“Playbooks,” “us,” “we,” or “our”) may collect, use, and share your personal information in connection
							with our software template marketplace available through our website located at www.playbooks.com,
							including all of its subdomains, webpages, and subpages, and any other websites or applications we own and
							operate that link to this Privacy Policy (collectively, “Website”), together with our products, services,
							social media pages, events, emails, and other electronic communications (for purposes of this Privacy
							Policy, collectively the “Service”), and the choices you have with respect to your personal information.
							Playbooks reserves the right, at any time, to modify this Privacy Policy. If we modify how we collect,
							use, or share personal information, we will post the updated Privacy Policy on this page. In some cases,
							we may notify you about an update by sending you an email, posting a notice about the update on the
							Service, or other means as may be required by applicable law. In all cases, your continued use of the
							Service following posting or other notification of changes constitutes your acknowledgement of such
							changes. Please review this Privacy Policy periodically to keep up to date on our most current policies
							and practices.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Personal Information You Provide</h2>
						<p className='text-sm text-foreground-body'>
							We collect personal information when you provide it to us, which may include the following:
						</p>
						<ul className='list-decimal pl-8'>
							<li className='gap-4 py-2 pl-4'>
								<b>Prospect information</b>, such as first and last name, email address, and social media webpages or
								handles.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Professional information</b>, such as current and prior employer name, job title, company location,
								and other details we may collect about your business or profession.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Education information</b>, such as the educational institution you attend, your grade level, or your
								anticipated year of graduation if you engage with the Service under a student subscription plan.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Account information</b>, such as your log-in details, profile picture, information that you add to
								your profile (e.g., interests, biographical information) or store in your account, information you
								provide during registration or agreement submission, software templates you purchase or create, and
								other details about your use of the Service.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Payment information</b>, such as bank account number, credit or debit card number, or financial
								account details used for payments. All payment processing services connected with your use of the
								Service is provided to you by third-party payment processors.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Transaction history</b>, such as subscription plans purchased from us and records of software
								templates you have created or purchased through the Service.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Preferences</b>, such as the information you provide in response to our surveys, questionnaires, or
								contests.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Communications</b>, such as the information associated with your requests or inquiries, including for
								support, assistance, or order information, and any feedback you provide when you communicate with us.
							</li>
						</ul>
						<p className='text-sm text-foreground-body'>
							Unless we specifically request it, we ask that you not provide us with any sensitive personal information
							(e.g., information related to racial or ethnic origin, political opinions, religion or other beliefs,
							health, biometrics or genetic characteristics, criminal background, or trade union membership) on or
							through the Service, or otherwise to us.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Personal Information from Third Parties</b> We may obtain personal information about you from third
							parties, such as social media platforms and other public sources, third parties that help us advertise our
							services and find new customers, joint marketing partners, event co-sponsors, and other third parties.
						</p>
						<p className='text-sm text-foreground-body'>
							We will also collect personal information, such as profile information, from third-party platforms that
							you connect to your account, such as when you transfer software templates to and from your Github account
							or when you sign up on the Website using your login details from a third-party account or connect your
							Playbooks account to a third-party platform using one of our integrations.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Personal Information Collected Automatically</b> We, our service providers, and our business partners
							may automatically log information about you, your computer or mobile device, and your activity over time
							on our Service and other sites and online services, such as:
						</p>
						<ul className='list-decimal pl-8'>
							<li className='gap-4 py-2 pl-4'>
								<b>Device data</b>, such as your computer or mobile device operating system type and version number,
								manufacturer and model, browser type, screen resolution, IP address, unique identifiers, the website you
								visited before browsing to our website, and general location information such as city, state, or
								geographic area.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Online activity data</b>, such as pages or screens you viewed, how long you spent on a page or
								screen, navigation paths between pages or screens, information about your activity on a page or screen,
								access times, and duration of access.
							</li>
						</ul>
						<p className='text-sm text-foreground-body'>
							Like many online services, we use the following technologies to facilitate some of our automatic data
							stack:
						</p>
						<ul className='list-decimal pl-8'>
							<li className='gap-4 py-2 pl-4'>
								<b>Cookies</b>, which are text files that websites store on a visitor‘s device to uniquely identify the
								visitor’s browser or to store information or settings in the browser for the purpose of helping you
								navigate between pages efficiently, remembering your preferences, enabling functionality, helping us
								understand user activity and patterns, and facilitating online advertising.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Web beacons</b>, also known as pixel tags or clear GIFs, which are typically used to demonstrate that
								a webpage or email was accessed or opened, or that certain content was viewed or clicked, typically to
								compile statistics about usage of websites and the success of marketing campaigns.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Local storage</b>, which is used to save data on a visitor’s device. We may use data from local
								storage to, for example, turn on web navigation, store multimedia preferences, customize what we show
								you based on your past interactions with our Service, and remember your preferences.
							</li>
							<li className='gap-4 py-2 pl-4'>
								<b>Session-replay technologies</b>, which are third-party software programs that we may use on the Site
								or in some locations of the Service to record a video replay of user’s interactions with the Service.
								The video replay may include users’ clicks, mouse movements, scrolls, mobile app touches, typing, and
								other activity taken during the session. We use these replays for research and development purposes,
								such as to help us troubleshoot problems with the Service, understand how users interact with and use
								the Service, and identify areas for improvement.
							</li>
						</ul>
						<p className='text-sm text-foreground-body'>
							<b>Referrals</b> Users of the Service may have the opportunity to refer friends, colleagues, or other
							prospects to us and share their prospect information with us. Please do not refer someone to us or share
							their prospect information with us unless you have their permission to do so.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Use of Personal Information</h2>
						<p className='text-sm text-foreground-body'>
							Playbooks uses personal information for the purposes set forth below and as otherwise described in this
							Privacy Policy or at the time of stack.
						</p>

						<p className='text-sm text-foreground-body'>
							<b>To Provide the Service</b> We may use personal information to provide our Service and related products
							and services, and to operate our business. For example, we use personal information to:
						</p>
						<ul className='list-decimal pl-8'>
							<li className='gap-4 py-2 pl-4'>
								process your transactions, including granting access to our Service and providing our services and
								marketplace technology;
							</li>
							<li className='gap-4 py-2 pl-4'>establish, manage, monitor, and maintain your account;</li>
							<li className='gap-4 py-2 pl-4'>improve and personalize your experience on or with the Service;</li>
							<li className='gap-4 py-2 pl-4'>
								verify your identity or determine your eligibility for offers or promotions;
							</li>
							<li className='gap-4 py-2 pl-4'>
								communicate with you, including providing notices about your account or transaction (e.g.,
								confirmations, invoices, technical notices, updates, and security alerts) and responding to any of your
								requests, feedback, or questions;
							</li>
							<li className='gap-4 py-2 pl-4'>prospect you by email or other form of communication;</li>
							<li className='gap-4 py-2 pl-4'>
								provide any contests, surveys, sweepstakes, or other promotions you participate in;
							</li>
							<li className='gap-4 py-2 pl-4'>provide maintenance and support; and</li>
							<li className='gap-4 py-2 pl-4'>fulfill any other purpose for which you provide personal information.</li>
						</ul>

						<p className='text-sm text-foreground-body'>
							<b>Research and Development</b> We may use your personal information for research and development
							purposes, including to study and improve the Service and our business, understand and analyze the usage
							trends and preferences of our users, and develop new features, functionality, products, and services. As
							part of these activities, we may create aggregated, de-identified, or other anonymous data from personal
							information we collect. We make personal information into anonymous data by removing information that
							makes the data personally identifiable to you. We may use this anonymous data and share it with third
							parties for our lawful business purposes.
						</p>

						<p className='text-sm text-foreground-body'>
							<b>Direct Marketing</b> We may send you Playbooks-related or other direct marketing communications as
							permitted by law, including materials, updates, information, special offers, and promotional material from
							us and our business partners. You may opt-out of our marketing communications as described in the “YOUR
							CHOICES” section below.
						</p>

						<p className='text-sm text-foreground-body'>
							<b>Interest-Based Advertising</b> We work with third-party advertising companies and social media
							companies to help us advertise our business and to display ads for our Service. These companies may use
							cookies and similar technologies to collect information about you (including the online activity
							information and device information described above in the section called “Personal Information
							Automatically Collected”) over time across our Service and other websites and services or your interaction
							with our emails, and use that information to serve ads that they think will interest you. In addition,
							some of these companies may use hashed customer lists that we share with them to deliver ads to you and to
							similar users on their platforms. You can learn more about your choices for limiting interest-based
							advertising in the “YOUR CHOICES” section below.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>To Comply with Laws and Regulation</b> We will use personal information as we believe necessary or
							appropriate to comply with applicable laws, lawful requests, and legal process, such as to respond to
							subpoenas or requests from government authorities.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>For Compliance, Fraud Prevention, and Safety</b> We may use personal information and disclose it to law
							enforcement, government authorities, and private parties as we believe necessary or appropriate to: (a)
							maintain the safety, security, and integrity of the Service, business, databases, and other technology
							assets; (b) protect our, your, or others’ rights, privacy, safety or property (including by making and
							defending legal claims); (c) audit our internal processes for compliance with legal and contractual
							requirements and internal policies; (d) enforce the terms and conditions that govern the Service; and (e)
							prevent, identify, investigate, and deter fraudulent, harmful, unauthorized, unethical or illegal
							activity, including cyberattacks and identity theft.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>With Your Consent</b> We will disclose your personal information in accordance with your prior
							direction or, in some cases, we may specifically ask you for your consent to collect, use, or share your
							personal information, such as when required by law.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Sharing of Personal Information</h2>
						<p className='text-sm text-foreground-body'>
							In addition to the sharing described elsewhere in this Privacy Policy or at the point of stack, Playbooks
							may share your personal information as follows:
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Related Companies</b> We may share your personal information with current or future affiliates,
							subsidiaries, and other related companies.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Service Providers</b> We may share your personal information with third parties who perform services on
							our behalf that are necessary for the orderly operation of our business. For example, we work with service
							providers that help us perform website hosting, maintenance services, database management, analytics,
							fraud protection, marketing, customer relationship management, finances, and other purposes.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Integration Partners</b>Certain features of the Service are designed to or may optionally be set to
							integrate with the products and services offered by third parties. If you are using the Service as part of
							an integrated platform, we may share your personal information with the third parties with whom our
							service integrates.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Advertising Partners</b>We may also share personal information with third parties who we partner with
							for advertising campaigns or that collect information about your activity on the Service for the purposes
							described in the “Interest-Based Advertising” section above.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Other Users of the Service or the Public</b>We provide functionality that enables you to disclose
							personal information to other users of the Service or the public. For instance, users maintain a user
							profile with a profile picture and information about yourself or your use of the Service that is made
							available to other users or the public. You are also able to submit content to the Service (such as
							software templates, comments, questions, and reviews), and we will identify you by displaying information
							such as your username, social media handle, or a link to your user profile along with the content you
							submit. We do not control how other users or third parties use any personal information that you make
							available to other users or the public.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Professional Advisors</b>We may share personal information with persons, companies, or professional
							firms providing Playbooks with advice and consulting in accounting, administrative, legal, tax, financial,
							debt stack, and other matters.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Law Enforcement, Government Authorities, and Private Parties</b>Under certain circumstances, we may be
							required to disclose personal information to law enforcement, government authorities, and other parties if
							required to do so by law or in response to valid requests by public authorities (e.g., a court or a
							government agency). We may also disclose personal information in the good faith belief that such action is
							necessary to comply with a legal obligation or for the purposes described above in the section titled “For
							Compliance, Fraud Prevention, and Safety.”
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Business Transaction Participants</b>We may disclose personal information to third parties in
							connection with any business transaction (or potential transaction) involving a merger, acquisition, sale
							of shares or assets, financing, consolidation, reworkspace, divestiture, or dissolution of all or a
							portion of our business (including in connection with a bankruptcy or similar proceedings).
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>International Transfers of Personal Information</h2>
						<p className='text-sm text-foreground-body'>
							Playbooks is based in the United States, and we have service providers in the United States and
							potentially other countries. Your personal information may be collected, used, and stored in these
							countries or other locations outside of your home country. Privacy laws in the locations where we handle
							your personal information may not be as protective as the privacy laws in your home country. By providing
							your personal information, where applicable law permits, you specifically and expressly consent to such
							transfer and processing and the stack, use, and disclosure set forth herein.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Your Choices</h2>
						<p className='text-sm text-foreground-body'>
							<b>Review and Request Changes to Your Account Personal Information</b>If you have a Playbooks account, you
							can log into your account to review, delete, and make changes to certain personal information stored in
							your account. If you need to make a change and are unable to do this through your account settings, please
							email us using the prospect information at the end of this Privacy Policy. We rely on you to update and
							correct the personal information contained in your account. Note that we may keep historical information
							in our backup files as permitted by law.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Marketing Emails</b>You may opt-out of marketing-related emails by clicking the “unsubscribe” link at
							the bottom of the email or by prospecting us as described below. You may still receive service-related
							communications, such as those relating to your account.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Cookies</b>Most browsers let you remove and/or stop accepting cookies from the websites you visit. To
							do this, follow the instructions in your browser’s settings. Many browsers accept cookies by default until
							you change your settings. If you do not accept cookies, however, you may not be able to use all
							functionality of the Service and it may not work properly. For more information about cookies, including
							how to see what cookies have been set on your browser and how to manage and delete them, visit
							www.allaboutcookies.org.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Analytics</b>We use Google Analytics to help us analyze how our website properties are being accessed
							and used. You can learn more about Google Analytics cookies by clicking here and about how Google protects
							your data by clicking here. To opt-out of Google Analytics, you can download and install the Google
							Analytics Opt-out Browser Add-on, available here.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Advertising Choices</b>You can limit the use of your information for interest-based advertising by
							blocking third-party cookies in your browser settings, using browser plug-ins/extensions, or using your
							mobile device settings to limit the use of the advertising ID associated with your mobile device. You can
							also opt out of interest-based ads from companies participating in the following industry opt-out programs
							by visiting the linked websites: the Network Advertising Initiative
							(http://www.networkadvertising.org/managing/opt_out.asp) and the Digital Advertising Alliance
							(https://optout.aboutads.info). Some of the companies we work with may offer their own opt-out mechanisms.
							For example, you can learn more about how Google uses cookies for advertising purposes by clicking here
							and opt-out of ad personalization by Google by clicking here.
						</p>
						<p className='text-sm text-foreground-body'>
							Many of the opt-out preferences described in this section must be set on each device or browser for which
							you want them to apply. Please note that some of the advertising companies we work with may not
							participate in the opt-out mechanisms described above, so even after opting-out, you may still receive
							interest-based advertisements from other companies. If you opt-out of interest-based advertisements, you
							will still see advertisements online but they may be less relevant to you.
						</p>
						<p className='text-sm text-foreground-body'>
							<b>Do Not Track</b>Some browsers may be configured to send “Do Not Track” signals to the online services
							that you visit. The Service currently does not respond to “Do Not Track” or similar signals. To find out
							more about “Do Not Track,” please visit http://www.allaboutdnt.com.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Children's Policy</h2>
						<p className='text-sm text-foreground-body'>
							The Service is not intended for use by anyone under the age of 13, and we do not knowingly collect
							personal information from minors under 13.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Security of Personal Information</h2>
						<p className='text-sm text-foreground-body'>
							No method of transmission over the Internet, or method of electronic storage, is fully secure. While we
							use reasonable efforts to protect your personal information from the risks presented by unauthorized
							access or acquisition, we cannot guarantee the security of your personal information.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Third Party Websites and Services</h2>
						<p className='text-sm text-foreground-body'>
							The Service may contain links to other websites and online services operated by third parties. These links
							are not an endorsement of, or representation that we are affiliated with, any third party. In addition,
							our content may be included on web pages or online services that are not associated with us. We do not
							control third party websites or online services, and we are not responsible for their actions. Other
							websites and services follow different rules regarding the stack, use, and sharing of your personal
							information. We encourage you to read the privacy policies of the other websites and online services you
							use.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Notice to California Residents (Shine the Light Law)</h2>
						<p className='text-sm text-foreground-body'>
							Under California Civil Code sections 1798.83-1798.84, California residents who have an established
							business relationship with Playbooks are entitled to ask us for a notice describing what categories of
							personal information we share with third parties for the third parties’ direct marketing purposes. If you
							are a California resident and would like a copy of this notice, please submit your request to the email
							address listed at the end of this Privacy Policy with “Shine the Light” in the subject line.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Notice to Nevada Residents</h2>
						<p className='text-sm text-foreground-body'>
							Nevada Revised Statutes Chapter 603A allows Nevada residents to opt-out of the “sale” of certain types of
							personal information. Subject to several exceptions, Nevada law defines “sale” to mean the exchange of
							certain types of personal information for monetary consideration to another person. If you are a Nevada
							resident who wishes to exercise your “sale” opt-out rights, you may submit a request to us using the email
							address listed at the end of this Privacy Policy with “Nevada Opt-Out Request” in the subject line.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Prospecting us</h2>
						<p className='text-sm text-foreground-body'>
							If you have any questions or concerns about our Privacy Policy or any other privacy matters, please
							prospect us at <SupportEmail />.
						</p>
					</DisplaySectionBody>
				</DisplaySection>
			</div>
		</>
	);
};

MktPrivacyRoute.getLayout = page => (
	<MktWrapper
		seo={{ description: 'Please review our privacy policy to learn how we protect, secure, and use your data.' }}>
		{page}
	</MktWrapper>
);

export default MktPrivacyRoute;
